import * as RadixSelect from '@radix-ui/react-select';
import { useEffect, useLayoutEffect, useRef, type ReactNode, type Ref } from 'react';

import { cn } from '@/lib/cn';

import { filterChipClass } from './FilterChip';
import { ICONS } from './icons';
import { stopPortalClick } from './portal-events';
import { usePortalTheme } from './use-portal-theme';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
  /**
   * ET GLYF FORAN NAVNET (QA13 runde 2, #15 og #32).
   *
   * #15: «Felttype velges med ikon + navn». #32: «Typens ikon vises i Type-nedtrekket, både i lista
   * og på valgt verdi.» Det er den samme kontrollen, og derfor den samme luka.
   *
   * Valgfritt, og de aller fleste listene i appen lar det stå tomt — et ikon per rad i en liste
   * over avdelinger ville vært tolv ganger det samme bildet. Det tjener bare den lista der radene
   * er TYPER: da er glyfet det man kjenner igjen, og ordet er det man leser når man er i tvil.
   *
   * EN `ReactNode` OG IKKE ET `IconName`, som var det første forsøket. En ressurstypes ikon er
   * DATA — `lucide:truck`, valgt av den som laget typen — og kan ikke uttrykkes av det lukkede
   * settet i `ICONS`. Kallstedet tegner sitt eget glyf; `ResourceTypeIcon` kjenner begge formene.
   *
   * Det vises i lista og i utløseren. `leading` under er den andre halvparten — ett fast ikon for
   * hele kontrollen — og de to skal ikke brukes samtidig.
   */
  glyph?: ReactNode;
  /**
   * ETT NIVÅ INN (QA13 runde 3, blokk 2 c).
   *
   * Atle: «Avdelingsvelgeren i Ny oppgave: vis treet med innrykk (hovedavdeling før
   * underavdelinger).»
   *
   * En `boolean` og ikke et tall, fordi avdelingstreet ER to nivåer og bare to — se
   * `department-levels.ts`, der refusjonen av det generelle treet står skrevet. En `depth: number`
   * ville invitert til en dybde tjenesten avviser.
   *
   * Innrykket tegnes bare i LISTA. Utløseren viser den rene etiketten: et valgt punkt står ikke
   * under noe, det står alene, og et innrykk der ville sett ut som en feil i justeringen.
   */
  indent?: boolean;
}

export interface SelectProps {
  options: readonly SelectOption[];
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  /** Shown when nothing is selected yet. Must come from a translation key. */
  placeholder?: string;
  disabled?: boolean;
  /**
   * A listbox has no read-only state — there is no text to leave selectable — so a read-only
   * select is a select you cannot open. `Field` still draws its padlock and its quieter
   * outline, which is what tells the difference from "disabled" on screen.
   */
  readOnly?: boolean;
  name?: string;
  required?: boolean;
  id?: string;
  className?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
  'data-testid'?: string;
  ref?: Ref<HTMLButtonElement>;
  /**
   * `field` er en kontroll i et skjema. `pill` er et FILTER over en liste.
   *
   * Forskjellen er ikke pynt, den er grammatikk. Et skjemafelt har en etikett over seg fordi det
   * skal fylles ut og etterpå leses tilbake; et filter har ingen «verdi å lese tilbake» — det er
   * enten av eller på, og når det er på ER verdien etiketten («Byrå» blir «Nordlyd Booking»).
   *
   * Fem `Field`-innrammede select-er på rad ga fem svevende etiketter over fem hakk i fem
   * omriss, og linja leste som et skjema man hadde glemt å fylle ut. Greenroom tegner dem som
   * piller, og det er derfor de forsvinner i bakgrunnen til man trenger dem.
   *
   * `quiet` er den tredje, og den er en KONTROLL I EN RAD (ARTISTS.md §0.5): «Row controls stay
   * quiet until pointed at. The responsible/assignee control is an avatar and a name with no
   * border; the border and caret appear on hover and on keyboard focus.»
   *
   * Forskjellen fra `pill` er igjen grammatikk og ikke pynt. Et filter må se PÅSLÅTT ut hele
   * tiden — et glemt filter er en liste med fire rader og ingen forklaring. En radkontroll er
   * motsatt: verdien er en opplysning man skanner nedover kolonnen, og tjue omriss under
   * hverandre gjør kolonnen til et skjema. Rammen er en affordans, og den trengs først i det
   * noen sikter på den.
   */
  variant?: 'field' | 'pill' | 'quiet';
  /** Ordet pillen viser når ingenting er valgt. Bare for `pill` — se `variant`. */
  pillLabel?: string;
  /**
   * Verdien som betyr «ingen filter» for en `pill` — «Alle statuser». Standard `''`. Et valg med
   * denne verdien er et valg, men ikke et påslått filter, og chipen skal ikke lyse (29.09: fire
   * chips sto i aksent på en ufiltrert liste).
   */
  allValue?: string;
  /**
   * Noe som står FORAN verdien inne i kontrollen — en avatar, et ikon, et ord.
   *
   * For `quiet`: en ansvarlig er et menneske, og et menneske uten ansiktet sitt er et navn i en
   * kolonne med tjue andre navn. Radix' egen `Value` tar bare tekst, så bildet legges her.
   *
   * For `pill` (UX-ordren A3): ORDET filteret filtrerer på — «Type · Kjøretøy». Det står her og
   * ikke i hver `option` fordi listen innenfor ellers gjentar «Type ·» tjue ganger for å få det
   * til å stå én gang i utløseren.
   */
  leading?: ReactNode;
  /**
   * Navnet når ingen `<label>` peker hit — filterchips i en verktøylinje. TypeScript sjekker ikke
   * JSX-attributter med bindestrek mot props, så uten denne linja ble `aria-label` godtatt og
   * stille kastet (29.09, fanget av S5 i Glass 2).
   */
  'aria-label'?: string;
}

/**
 * The select, drawn by Tvitvi rather than by the operating system.
 *
 * WHY THIS IS NOT A NATIVE `<select>` ANY MORE. Atle opened one in dark mode and got a white
 * popup with near-invisible grey options and an OS-blue highlight. That is not a CSS bug that
 * was left unfixed: a native select's option list is painted by the platform, outside the page,
 * and `color-scheme: dark` tints the closed control only. There is no rule you can write that
 * reaches the popup. DESIGN.md §10 is blunt about the result — "Komponentene skal ikke se ut
 * som rå bibliotek-standarder... tegner vi flaten selv" — so the behaviour comes from Radix and
 * the surface is ours.
 *
 * WHAT WE KEEP FROM THE NATIVE ONE. Type-ahead, ArrowUp/ArrowDown/Home/End, Escape to close,
 * the value announced as a combobox, and a hidden form-associated control so a `name` still
 * submits. Radix gives all of that; none of it is re-implemented here.
 *
 * THE TRIGGER IS A FIELD. It wears `.field-control`, so inside a `Field` it is the same height,
 * the same inset and the same notched outline as a text input — a form does not change grammar
 * halfway down. `<label htmlFor>` names it: a `<button>` is a labelable element, so the
 * association `Field` already had is unchanged.
 */
export function Select({
  options,
  value,
  defaultValue,
  onValueChange,
  placeholder,
  disabled = false,
  readOnly = false,
  name,
  required,
  id,
  className,
  ref,
  variant = 'field',
  leading,
  pillLabel,
  allValue = '',
  'aria-label': ariaLabel,
  'aria-describedby': describedBy,
  'aria-invalid': invalid,
  'data-testid': testId,
}: SelectProps): ReactNode {
  const portal = usePortalTheme();
  const Chevron = ICONS.chevronDown;
  const Check = ICONS.check;
  const ScrollUp = ICONS.sortAsc;
  const ScrollDown = ICONS.sortDesc;
  /* `defaultValue` teller med: en ustyrt kontroll har også et valgt glyf å vise. */
  const chosen = options.find((option) => option.value === (value ?? defaultValue));

  /*
   * EN VERDI SATT UTENFRA ER IKKE ET VALG (spillested/11, 29.09).
   *
   * Står kontrollen i et `<form>` — og kroppen i hver `Sheet` er ett — tegner Radix et skjult
   * `<select>` ved siden av. Endres `value` utenfra, setter Radix verdien på det feltet i en effekt
   * og sender `change`, og feltet svarer med sin EGEN verdi. Rett etter første tegning har det ingen
   * alternativer ennå, så svaret er `''`, og det kom hit som om noen hadde valgt det: budskjemaet i
   * artistkortet fylte inn standardvilkårene, alle seks ytelsene ble `''` og budet fikk 400.
   *
   * Ekkoet kommer i barnets effekt, i samme runde som `value` endret seg: layout-effekten under
   * åpner vinduet før noen passiv effekt kjører, og vår egen passive effekt — som kjører etter
   * barnas — lukker det. Et ekte valg kommer fra en hendelse, aldri midt i den runden. Og et valg
   * som ikke er ett av alternativene er ikke et valg uansett hvor det kom fra.
   */
  const echoWindow = useRef(false);
  useLayoutEffect(() => {
    echoWindow.current = true;
  }, [value]);
  useEffect(() => {
    echoWindow.current = false;
  }, [value]);

  return (
    <RadixSelect.Root
      value={value}
      defaultValue={defaultValue}
      /* Se `echoWindow` over. */
      onValueChange={(next) => {
        if (echoWindow.current) return;
        if (options.some((option) => option.value === next)) onValueChange?.(next);
      }}
      disabled={disabled || readOnly}
      name={name}
      required={required}
      /*
       * FUNN 18s ANDRE HALVDEL ER IKKE RETTET HER, og det står fordi gjetningen var feil.
       *
       * QA: «ett klikk åpner ikke alltid panelet … sannsynligvis fordi et åpent dropdown/fokus
       * spiser første klikk». Det stemmer som beskrivelse — Radix' `DismissableLayer` sluker
       * klikket som lukker lista — men `Select` har ingen `modal`-prop å slå av, i motsetning til
       * `DropdownMenu`. Å tvinge det ville krevd å overstyre `onPointerDownOutside` på `Content`,
       * og en dialog som lukkes av et klikk den også sender videre er to hendelser fra én.
       *
       * A3 fjerner grunnen framfor symptomet: den fullbreddes typevelgeren som lå over lista blir
       * en filterchip i verktøylinja, og da er det ikke lenger en åpen nedtrekksliste mellom
       * fingeren og raden. Måles på nytt i QA-runden.
       */
      onOpenChange={(open) => {
        if (open) portal.capture();
      }}
    >
      <RadixSelect.Trigger
        id={id}
        ref={(node) => {
          portal.anchorRef(node);
          if (typeof ref === 'function') ref(node);
          else if (ref) ref.current = node;
        }}
        aria-label={ariaLabel}
        aria-describedby={describedBy}
        aria-invalid={invalid}
        data-testid={testId}
        /*
         * `field`-varianten får rammen sin av `.field-frame` RUNDT seg, ikke av seg selv — derfor
         * er den unntatt fra knappevakten (`buttons-look-like-buttons.spec.ts`). `pill` og `quiet`
         * har sin egen kant og måles som alle andre kontroller.
         */
        {...(variant === 'field' ? { 'data-unstyled': true } : {})}
        className={cn(
          variant === 'quiet'
            ? [
                'ring-focus group inline-flex items-center gap-2 rounded-full border px-2 py-0.5',
                'border-transparent bg-transparent text-sm text-default',
                'transition-colors duration-[var(--duration-fast)]',
                /* Fyllet kommer når man sikter — med mus, og like mye med tastatur. Tonalt og uten
                   kant (G6), som en chip. */
                'hover:bg-[var(--fill-tonal)] focus-visible:bg-[var(--fill-tonal)]',
                'data-[state=open]:bg-[var(--fill-tonal)]',
                'data-[placeholder]:text-subtle',
              ]
            : variant === 'pill'
              ? /*
                 * EN FILTERCHIP (§16.5 punkt 4). Et påslått filter ser påslått ut — aksentfyll — og
                 * uten det er et filter man har glemt et filter man leter etter i ti minutter.
                 */
                filterChipClass(chosen !== undefined && chosen.value !== allValue)
              : ['field-control ring-focus flex items-center justify-between gap-2 text-start'],
          variant === 'field' && 'data-[placeholder]:text-subtle',
          className,
        )}
      >
        {/* `truncate` on the value, never on the control: a long stage name shortens, the
            chevron never moves, and the outline stays the width of the field. */}
        {leading}
        {/*
          UTLØSEREN VISER DET VALGTE GLYFET. Uten dette står ikonet bare i den åpne lista, og den
          lukkede kontrollen sier ikke hvilken type feltet ER — bare hva det heter. Det er den
          lukkede tilstanden man ser nitten av tjue ganger.
        */}
        {chosen?.glyph === undefined ? null : (
          <span className="flex size-3.5 shrink-0 items-center justify-center text-subtle">
            {chosen.glyph}
          </span>
        )}
        <span
          className={cn(
            'min-w-0 truncate',
            variant === 'pill' || variant === 'quiet' ? '' : 'flex-1',
          )}
        >
          {/*
           * Pillen viser ORDET når ingenting er valgt («Byrå»), og VERDIEN når noe er
           * («Nordlyd Booking»). Radix' `placeholder` gjør nøyaktig det, så det er ett felt og
           * ikke to tilstander å holde i synk.
           */}
          <RadixSelect.Value placeholder={variant === 'pill' ? pillLabel : placeholder} />
        </span>
        <RadixSelect.Icon asChild>
          <Chevron
            aria-hidden
            className={cn(
              'size-3.5 shrink-0',
              /* Chipens pil tar chipens farge når filteret er på; ellers dempet. */
              variant === 'pill' && chosen !== undefined ? null : 'text-subtle',
              /*
               * KARETEN ER USYNLIG TIL MAN SIKTER, men den OPPTAR PLASSEN sin hele tiden:
               * `opacity` og ikke `hidden`. Ellers hopper navnet to piksler til siden i det
               * musa kommer inn, og en kolonne som rykker er en kolonne som irriterer.
               */
              variant === 'quiet' &&
                'opacity-0 transition-opacity duration-[var(--duration-fast)] group-hover:opacity-100 group-focus-visible:opacity-100 group-data-[state=open]:opacity-100',
            )}
          />
        </RadixSelect.Icon>
      </RadixSelect.Trigger>

      <RadixSelect.Portal>
        <RadixSelect.Content
          // Stamped with the colour mode of the pane the trigger lives in, because this content
          // is portalled out of it. See `usePortalTheme`.
          {...portal.attributes}
          // Choosing an option must not also activate the row the select sits in.
          onClick={stopPortalClick}
          position="popper"
          sideOffset={4}
          collisionPadding={8}
          className={cn(
            /* Popoveren er navigasjonslaget: glass over innholdet (§16.3). */
            'glass-popover z-50 overflow-hidden text-default',
            'min-w-[var(--radix-select-trigger-width)]',
            'max-h-[var(--radix-select-content-available-height)]',
          )}
        >
          <RadixSelect.ScrollUpButton className="flex h-5 items-center justify-center text-subtle">
            <ScrollUp aria-hidden className="size-3" />
          </RadixSelect.ScrollUpButton>
          <RadixSelect.Viewport className="p-1">
            {options.map((option) => (
              <RadixSelect.Item
                key={option.value}
                value={option.value}
                disabled={option.disabled}
                className={cn(
                  // --row-height, the same 36px a table row uses. A full-width row has width to
                  // compensate with, which is the reasoning DESIGN.md §9's 44px floor already
                  // records for rows as against buttons.
                  'flex h-[var(--row-height)] cursor-pointer items-center gap-2',
                  'rounded-[var(--radius-menu-row)] px-2 text-sm text-default outline-none select-none',
                  'data-[highlighted]:bg-[var(--fill-tonal)]',
                  // Selected: a tinted row AND a check glyph. The glyph is the half that
                  // survives a monochrome screen (DESIGN.md §3).
                  'data-[state=checked]:bg-accent-subtle data-[state=checked]:font-medium',
                  'data-[state=checked]:data-[highlighted]:bg-accent-subtle',
                  'data-[disabled]:pointer-events-none data-[disabled]:opacity-50',
                  // Ett nivå inn, på selve raden og ikke som tegn i teksten: et innrykk laget av
                  // mellomrom er noe en skjermleser leser opp og et søk må skrive.
                  option.indent === true && 'pl-6',
                )}
              >
                <span className="flex size-3.5 shrink-0 items-center justify-center">
                  <RadixSelect.ItemIndicator asChild>
                    <Check aria-hidden className="size-3.5 text-[color:var(--color-accent-text)]" />
                  </RadixSelect.ItemIndicator>
                </span>
                {option.glyph === undefined ? null : (
                  <span className="flex size-3.5 shrink-0 items-center justify-center text-subtle">
                    {option.glyph}
                  </span>
                )}
                <RadixSelect.ItemText>{option.label}</RadixSelect.ItemText>
              </RadixSelect.Item>
            ))}
          </RadixSelect.Viewport>
          <RadixSelect.ScrollDownButton className="flex h-5 items-center justify-center text-subtle">
            <ScrollDown aria-hidden className="size-3" />
          </RadixSelect.ScrollDownButton>
        </RadixSelect.Content>
      </RadixSelect.Portal>
    </RadixSelect.Root>
  );
}
